import { API_BASE } from "@/lib/api";

export type TienditaBundle = {
  workspace: {
    id: string;
    name: string;
    plan: "free" | "starter" | "pro" | "business";
    ownerEmail?: string;
    ownerPhone?: string;
    settings?: Record<string, unknown>;
  };
  profile: Record<string, unknown>;
  limits: Record<string, unknown>;
  metrics: Record<string, number | string | boolean>;
  dailySummary?: Record<string, unknown>;
  setupChecklist: Array<{ id: string; label: string; done: boolean }>;
  products: Array<Record<string, unknown>>;
  carts?: Array<Record<string, unknown>>;
  customers: Array<Record<string, unknown>>;
  orders: Array<Record<string, unknown>>;
  receipts: Array<Record<string, unknown>>;
  loyaltyPrograms: Array<Record<string, unknown>>;
  loyaltyEntries: Array<Record<string, unknown>>;
  broadcasts: Array<Record<string, unknown>>;
  integrations: Array<Record<string, unknown>>;
  orderEvents?: Array<Record<string, unknown>>;
  inventoryMovements?: Array<Record<string, unknown>>;
  paymentEvents?: Array<Record<string, unknown>>;
  statusChecks?: Array<Record<string, unknown>>;
  auditLogs?: Array<Record<string, unknown>>;
  recentRuns: Array<Record<string, unknown>>;
  recentMessages: Array<Record<string, unknown>>;
  publicStore?: {
    slug: string;
    publicLink: string;
    qrPayload: string;
    enabled: boolean;
  };
  templates?: Array<Record<string, unknown>>;
  billing?: {
    active: boolean;
    subscription?: Record<string, unknown>;
    canManageBilling: boolean;
  };
};

export type AccountContext = {
  user?: { id: string; email: string; phone?: string };
  memberships: Array<{ productKind: "web" | "suite"; workspaceId: string; role: string; status: string }>;
  subscriptions: Array<Record<string, unknown>>;
};

export async function apiFetch<T>(path: string, options: RequestInit = {}, token = ""): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has("content-type") && options.body) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || body.message || `API error ${response.status}`);
  return body as T;
}

export function requestMagicLink(email: string, returnPath = "/tienditabot") {
  return apiFetch<{ success: boolean; token?: string; verifyUrl?: string; delivered?: boolean; delivery?: string }>("/account/auth/request-link", {
    method: "POST",
    body: JSON.stringify({ email, returnPath }),
  });
}

export function verifyMagicToken(token: string) {
  return apiFetch<{ success: boolean; sessionToken: string; user: { id: string; email: string } }>("/account/auth/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function getAccount(token: string) {
  return apiFetch<AccountContext>("/account/me", {}, token);
}

export function createTienditaWorkspace(token: string, payload: Record<string, unknown>) {
  return apiFetch<{ success: boolean; workspace: { id: string }; profile: Record<string, unknown>; sessionToken?: string; bundle: TienditaBundle }>(
    "/products/products/tiendita-bot-os/onboarding",
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function listMyTienditaWorkspaces(token: string) {
  return apiFetch<{ workspaces: Array<{ id: string; name: string; plan: string; productId: string }> }>(
    "/products/products/tiendita-bot-os/workspaces/mine",
    {},
    token,
  );
}

export function getTienditaBundle(token: string, workspaceId: string) {
  return apiFetch<TienditaBundle>(`/products/workspaces/${workspaceId}/tiendita`, {}, token);
}

export function patchTienditaProfile(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<{ success: boolean; profile: Record<string, unknown>; bundle: TienditaBundle }>(
    `/products/workspaces/${workspaceId}/tiendita/profile`,
    { method: "PATCH", body: JSON.stringify(payload) },
    token,
  );
}

export function configureTienditaPublicStore(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<{ success: boolean; publicStore: Record<string, unknown>; bundle: TienditaBundle }>(
    `/products/workspaces/${workspaceId}/tiendita/public-store`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function importTienditaCatalogText(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<{ success: boolean; imported: number; products: Array<Record<string, unknown>>; bundle: TienditaBundle }>(
    `/products/workspaces/${workspaceId}/tiendita/import-text`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function createTienditaEntity(token: string, workspaceId: string, collection: "products" | "customers" | "orders" | "receipts" | "broadcasts", payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/${collection}`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function searchTienditaProducts(token: string, workspaceId: string, query: string) {
  return apiFetch<{ products: Array<Record<string, unknown>> }>(
    `/products/workspaces/${workspaceId}/tiendita/search?q=${encodeURIComponent(query)}`,
    {},
    token,
  );
}

export function createTienditaCart(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<{ cart: Record<string, unknown> }>(
    `/products/workspaces/${workspaceId}/tiendita/carts`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function addTienditaCartItem(token: string, workspaceId: string, cartId: string, payload: Record<string, unknown>) {
  return apiFetch<{ cart: Record<string, unknown> }>(
    `/products/workspaces/${workspaceId}/tiendita/carts/${cartId}/items`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function checkoutTienditaCart(token: string, workspaceId: string, cartId: string, payload: Record<string, unknown>) {
  return apiFetch<{ success: boolean; cart: Record<string, unknown>; order: Record<string, unknown>; receipt: Record<string, unknown> }>(
    `/products/workspaces/${workspaceId}/tiendita/carts/${cartId}/checkout`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function runTienditaCommerceTool(token: string, workspaceId: string, tool: "consultar_precio" | "agregar_al_carrito" | "verificar_cobertura" | "checkout", payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/commerce-tools/${tool}`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function confirmTienditaManualPayment(token: string, workspaceId: string, orderId: string, payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/orders/${orderId}/manual-payment`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function queueTienditaAbandonmentReminders(token: string, workspaceId: string, payload: Record<string, unknown> = {}) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/abandonment-reminders`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function getTienditaMarketplace(token: string, workspaceId: string) {
  return apiFetch<{ integrations: Array<Record<string, unknown>> }>(
    `/products/workspaces/${workspaceId}/tiendita/marketplace`,
    {},
    token,
  );
}

export function updateTienditaEntity(token: string, workspaceId: string, collection: "products" | "customers" | "orders" | "broadcasts", entityId: string, payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/${collection}/${entityId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
    token,
  );
}

export function createLoyaltyProgram(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/loyalty/programs`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function addLoyaltyStamp(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/loyalty/stamps`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function upsertIntegration(token: string, workspaceId: string, payload: Record<string, unknown>) {
  return apiFetch<Record<string, unknown>>(
    `/products/workspaces/${workspaceId}/tiendita/integrations`,
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export function startSubscriptionCheckout(token: string, payload: Record<string, unknown>) {
  return apiFetch<{ checkoutUrl?: string; sessionToken?: string; subscription?: Record<string, unknown>; workspace?: { id: string } }>(
    "/account/subscriptions/checkout",
    { method: "POST", body: JSON.stringify({ productKind: "suite", productId: "tiendita-bot-os", returnPath: "/tienditabot", ...payload }) },
    token,
  );
}

export function getPublicTienditaCatalog(slug: string) {
  return apiFetch<{
    store: Record<string, unknown>;
    products: Array<Record<string, unknown>>;
    orderEndpoint: string;
  }>(`/products/tiendita/public/${encodeURIComponent(slug)}/catalog`);
}

export function createPublicTienditaQuote(slug: string, payload: Record<string, unknown>) {
  return apiFetch<{
    success: boolean;
    quote: Record<string, unknown>;
  }>(
    `/products/tiendita/public/${encodeURIComponent(slug)}/quote`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function createPublicTienditaOrder(slug: string, payload: Record<string, unknown>) {
  return apiFetch<{
    success: boolean;
    customer?: Record<string, unknown>;
    order?: Record<string, unknown>;
    receipt?: Record<string, unknown>;
    whatsappMessage?: string;
    paymentInstructions?: string;
    paymentLink?: string;
    orderStatusUrl?: string;
  }>(
    `/products/tiendita/public/${encodeURIComponent(slug)}/orders`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function createPublicTienditaOrderCheckout(slug: string, orderId: string, payload: Record<string, unknown>) {
  return apiFetch<{
    success: boolean;
    reused?: boolean;
    provider?: string;
    checkoutStatus?: string;
    checkoutUrl?: string;
    paymentLink?: string;
    paymentToken?: string;
    order?: Record<string, unknown>;
    paymentEvent?: Record<string, unknown>;
    orderStatusUrl?: string;
  }>(
    `/products/tiendita/public/${encodeURIComponent(slug)}/orders/${encodeURIComponent(orderId)}/checkout`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function getPublicTienditaOrderStatus(slug: string, orderId: string, phone = "", token = "") {
  const params = new URLSearchParams();
  if (phone) params.set("phone", phone);
  if (token) params.set("t", token);
  const query = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<{
    order: Record<string, unknown>;
    receipt?: Record<string, unknown>;
    events: Array<Record<string, unknown>>;
  }>(`/products/tiendita/public/${encodeURIComponent(slug)}/orders/${encodeURIComponent(orderId)}${query}`);
}
